import { format, isValid } from 'date-fns';

/**
 * Formats the epoch-seconds timestamps the platform sends (e.g. 1788251565.880518669).
 * Mirrors the transaction-history formatting so dates read the same across the app.
 */
export function formatEpochDateTime(epochSeconds) {
  if (epochSeconds === null || typeof epochSeconds === 'undefined' || epochSeconds === '')
    return '';
  const seconds =
    typeof epochSeconds === 'string' ? parseFloat(epochSeconds) : Number(epochSeconds);
  if (!Number.isFinite(seconds)) return '';
  const date = new Date(seconds * 1000);
  return isValid(date) ? format(date, "MMM d, yyyy 'at' h:mm a") : '';
}

const hasFields = (entry) => Boolean(entry) && Object.keys(entry).length > 0;

const toList = (value) => (Array.isArray(value) ? value.filter(hasFields) : []);

/**
 * Derives the post-exchange state from a next-step / GET /application response.
 *
 * `process_application` and `previous_applications` both arrive as arrays — one entry per
 * exchange the customer has been through. The leading `process_application` entry is the
 * request the screens talk about, and every `previous_applications` entry gets its own card.
 *
 * The presence of a previous application is the only signal that matters: whenever the
 * platform sends one, that application stays funded and active until this new one is funded.
 * The exchange trigger status and any provider error are deliberately ignored — an exchange
 * the provider rejected leaves the previous applications in exactly the same place, so it is
 * surfaced the same way rather than as a failure.
 */
export function getExchangeState(application) {
  const {
    process_application: processApplications,
    previous_applications: previousApplicationList,
  } = application || {};

  const [latestRequest] = toList(processApplications);
  const previousApplications = toList(previousApplicationList);
  const isPostExchange = previousApplications.length > 0;

  return {
    isPostExchange,
    requestedAt: isPostExchange ? formatEpochDateTime(latestRequest?.date_time_requested) : '',
    previousApplications,
  };
}

/** The per-application identifier the platform sends on `previous_applications`. */
export const getApplicationRef = (application) => application?.request_no;
